/**
 * Workspace's tabs view (glossary: Workspace views): the Chats page's list
 * (chip bar, chat rows, header search) across the whole Scope. The page owns
 * the data, the menus and the chat drag; this draws the list.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaUrl } from '../chat/media';
import { BuyingPowerDots, TemperatureRing } from '../chats/RowBadges';
import { TabEmpty, TabRows, type RowDrag, type RowView } from '../chats/TabRow';
import { readCrmStyling } from '../crm/crmStyling';
import { FeatherIcon } from '../components/FeatherIcon';
import type { CrmAccount } from '../crm/crmAccess';
import type { SearchHit } from './api';
import { chatKey } from './chats';
import type { AccountColour } from './colour';
import { useFanExtra } from './fanExtras';
import { badgeFor, WORKSPACE_TABS, type WorkspaceTab } from './workspaceView';
import { useFanSearch } from './useFanSearch';
import { useSeen } from '../hooks/useSeen';
import { useMouseDragScroll } from './useMouseDragScroll';
import { Avatar } from './widgets/Avatar';
import type { CardItem } from './widgets/ChatCollection';
import { AccountDropdown } from './widgets/Dropdown';
import { NO_AUTOFILL } from './widgets/SearchWidget';

export interface TabsDrag {
  /** Pointer pressed on a row (`pinned` when it is a row of the Pinned tab). */
  start: (card: Pick<CardItem, 'accountId' | 'fanId' | 'fanName' | 'fanUsername' | 'fanAvatar'>, fromPinned: boolean, e: PointerEvent, key: string) => void;
  draggingKey: string | null;
  /** A chat is being dragged somewhere on the page. */
  active: boolean;
  /** …and it is over a Pinned drop zone. */
  overPinned: boolean;
}

interface Props {
  tab: WorkspaceTab;
  onTab: (tab: WorkspaceTab) => void;
  cards: Record<WorkspaceTab, CardItem[]>;
  loading: Record<WorkspaceTab, boolean>;
  repliedCount: number;
  /** The current tab's controls, right of the subrow (the page owns their menus). */
  tools: ReactNode;
  feed: { error: boolean; retry: () => void; bringBack: () => void };
  activity: { newCount: number; freeze: () => void; thaw: () => void; reconnecting: boolean };
  /** Scope is one Account: rows drop the model avatar and name. */
  singleAccount: boolean;
  colourOf: (accountId: string) => AccountColour;
  openKey: string | null;
  onOpen: (card: CardItem) => void;
  onOpenHit: (hit: SearchHit) => void;
  drag: TabsDrag;
  search: { accounts: CrmAccount[]; accountId: string | null; onAccount: (id: string) => void; accountName: (id: string) => string; request: number };
}

/**
 * The fan's avatar in its temperature ring, with the model's small avatar on
 * its corner when rows mix Accounts. Avatar, temperature and buying power are
 * fetched only once the row is near the screen (a long list would queue them
 * all at once).
 */
function RowAvatar({ accountId, fanId, src, name, model, buyingPower }: { accountId: string; fanId: string; src: string | null | undefined; name: string; model: { name: string; avatar: string | null | undefined; accent: string } | null; buyingPower: boolean }) {
  const { ref, seen } = useSeen<HTMLSpanElement>();
  const media = useMediaUrl(accountId, src || null, seen);
  const extra = useFanExtra(accountId, fanId, { ask: seen, buyingPower });
  const [failed, setFailed] = useState<string | null>(null);
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <>
      <span className="chat-avatar" ref={ref}>
        {media.url && failed !== media.url ? <img src={media.url} alt="" loading="lazy" draggable={false} onError={() => setFailed(media.url)} /> : <span>{initials}</span>}
      </span>
      <TemperatureRing value={extra?.temperature} />
      {model && <Avatar className="ws-tab-model-av" src={model.avatar} name={model.name} size={16} ring={`0 0 0 2px var(--bg-primary), 0 0 0 3.5px ${model.accent}`} />}
    </>
  );
}

/** The fan's buying power dots (the row's avatar asks for them). */
function RowBuyingPower({ accountId, fanId }: { accountId: string; fanId: string }) {
  const extra = useFanExtra(accountId, fanId, { ask: false, buyingPower: true });
  return <BuyingPowerDots value={extra?.buyingPower} known={extra?.buyingPower !== undefined} />;
}

function avatarKey(accountId: string, src: string | null | undefined, name: string, model: string | null) {
  return `${accountId}|${src ?? ''}|${name}|${model ?? ''}`;
}

export const TabsView = memo(function TabsView(p: Props) {
  const { t } = useTranslation('workspace');
  const [searchOpen, setSearchOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const chipDrag = useMouseDragScroll();
  const query = searchOpen ? typed.trim() : '';
  const { hits, busy, error } = useFanSearch(p.search.accountId, query);

  // `/` (the page's shortcut) opens search and puts the cursor in it.
  const firstRequest = useRef(p.search.request);
  useEffect(() => {
    if (p.search.request === firstRequest.current) return;
    setSearchOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [p.search.request]);

  const closeSearch = () => {
    setSearchOpen(false);
    setTyped('');
  };

  // The person's Styling switch, as on the Chats page's list.
  const buyingPower = useMemo(() => readCrmStyling().flags.buyingPower, []);
  const chipsFor = useCallback((accountId: string, fanId: string) => (buyingPower ? <RowBuyingPower accountId={accountId} fanId={fanId} /> : undefined), [buyingPower]);

  const cards = p.cards[p.tab];
  const byKey = useMemo(() => new Map(cards.map((c) => [c.key, c])), [cards]);
  const rows = useMemo(
    () =>
      cards.map(
        (c): RowView => ({
          key: c.key,
          fanId: c.fanId,
          name: c.fanName,
          avatar: <RowAvatar accountId={c.accountId} fanId={c.fanId} src={c.fanAvatar} name={c.fanName} model={p.singleAccount ? null : { name: c.modelName, avatar: c.modelAvatar, accent: c.accent }} buyingPower={buyingPower} />,
          chips: chipsFor(c.accountId, c.fanId),
          avatarKey: avatarKey(c.accountId, c.fanAvatar, c.fanName, p.singleAccount ? null : `${c.modelName}|${c.modelAvatar ?? ''}|${c.accent}`),
          model: p.singleAccount ? null : c.modelName,
          badge: badgeFor(p.tab, c, t),
          preview: c.sub,
          time: c.time,
          dim: c.dim,
          action: c.action ? { icon: c.action.icon, label: c.action.tip, run: c.action.run } : undefined,
          group: p.tab === 'active' ? c.group?.label ?? null : null,
        }),
      ),
    [cards, p.tab, p.singleAccount, t, buyingPower, chipsFor],
  );

  const { search, colourOf, singleAccount } = p;
  const hitRows = useMemo(
    () =>
      hits.map(
        (h): RowView => {
          const model = search.accountName(h.account_id);
          const accent = colourOf(h.account_id).accent;
          const account = search.accounts.find((a) => a.account_id === h.account_id);
          return {
            key: `s:${chatKey(h.account_id, h.fan_id)}`,
            fanId: h.fan_id,
            name: h.fan_name,
            avatar: <RowAvatar accountId={h.account_id} fanId={h.fan_id} src={h.fan_avatar} name={h.fan_name} model={singleAccount ? null : { name: model, avatar: account?.avatar_url, accent }} buyingPower={buyingPower} />,
            chips: chipsFor(h.account_id, h.fan_id),
            avatarKey: avatarKey(h.account_id, h.fan_avatar, h.fan_name, singleAccount ? null : `${model}|${account?.avatar_url ?? ''}|${accent}`),
            model: singleAccount ? null : model,
            badge: h.spent != null && h.spent > 0 ? { icon: 'dollar-sign', label: t('tabsView.badge.spent'), amount: `$${h.spent.toLocaleString('en-US')}`, tone: 'accent' } : undefined,
            preview: h.snippet || (h.fan_username ? `@${h.fan_username}` : ''),
            time: '',
          };
        },
      ),
    [hits, search, colourOf, singleAccount, t, buyingPower, chipsFor],
  );
  const hitByKey = useMemo(() => new Map(hits.map((h) => [`s:${chatKey(h.account_id, h.fan_id)}`, h])), [hits]);

  const rowDrag: RowDrag = {
    draggingKey: p.drag.draggingKey,
    start: (row, e) => {
      const card = byKey.get(row.key);
      if (card) return p.drag.start(card, p.tab === 'pinned', e, row.key);
      const h = hitByKey.get(row.key);
      if (h) p.drag.start({ accountId: h.account_id, fanId: h.fan_id, fanName: h.fan_name, fanUsername: h.fan_username || null, fanAvatar: h.fan_avatar }, false, e, row.key);
    },
  };
  const isActive = (row: RowView) => {
    const card = byKey.get(row.key);
    const h = card ? null : hitByKey.get(row.key);
    const key = card ? chatKey(card.accountId, card.fanId) : h ? chatKey(h.account_id, h.fan_id) : null;
    return !!key && key === p.openKey;
  };

  const empty = (() => {
    switch (p.tab) {
      case 'activity':
        return <TabEmpty icon="activity" title={t('activity.emptyTitle')} text={t('activity.emptyText')} />;
      case 'suggestions':
        return p.feed.error ? (
          <TabEmpty icon="wifi-off" title={t('feed.errorTitle')} text={t('feed.errorText')}>
            <button type="button" className="chat-tab-empty-link" onClick={p.feed.retry}>
              {t('feed.retry')}
            </button>
          </TabEmpty>
        ) : (
          <TabEmpty icon="sparkles" title={t('feed.emptyTitle')} text={t('feed.emptyText')}>
            <button type="button" className="chat-tab-empty-link" onClick={p.feed.bringBack}>
              {t('feed.bringBack')}
            </button>
          </TabEmpty>
        );
      case 'active':
        return <TabEmpty icon="messages-square" title={t('active.emptyTitle')} text={t('active.emptyText')} />;
      case 'replied':
        return <TabEmpty icon="message-circle" title={t('tabsView.repliedEmptyTitle')} text={t('tabsView.repliedEmptyText')} />;
      case 'pinned':
        return <TabEmpty icon="pin" title={t('pinned.emptyTitle')} text={t('tabsView.pinnedEmptyText')} />;
    }
  })();

  const searching = !!query;
  const pinnedTab = p.tab === 'pinned' && !searching;
  const dropClass = (on: boolean) => (p.drag.active ? (on ? 'is-drop' : 'is-drop-ready') : '');

  return (
    <div className="chats-panel ws-tabs">
      <div className={`chats-panel-head ${searchOpen ? 'search-open' : ''}`}>
        <div className="chats-panel-head-main">
          <span className="chats-panel-title">{t('tabsView.title')}</span>
          <span className="chats-panel-tools">
            <button type="button" className="chats-panel-tool" aria-label={t('tabsView.searchOpen')} title={`${t('tabsView.searchOpen')} (/)`} onClick={() => setSearchOpen(true)}>
              <FeatherIcon name="search" size={18} />
            </button>
          </span>
        </div>
        {searchOpen && (
          <div className="chats-panel-head-search ws-tabs-search">
            <AccountDropdown accounts={p.search.accounts} value={p.search.accountId ?? ''} onChange={p.search.onAccount} colourOf={p.colourOf} />
            <span className="chats-head-search-wrap">
              <input
                {...NO_AUTOFILL}
                ref={inputRef}
                name="ws-tabs-fan-search"
                role="searchbox"
                className="chats-head-search-input"
                autoFocus
                value={typed}
                placeholder={t('tabsView.searchPlaceholder')}
                aria-label={t('tabsView.searchOpen')}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    closeSearch();
                  } else if (e.key === 'Enter' && hits[0]) p.onOpenHit(hits[0]);
                }}
              />
            </span>
            <button type="button" className="chats-panel-tool" aria-label={t('tabsView.searchClose')} title={t('tabsView.searchClose')} onClick={closeSearch}>
              <FeatherIcon name="x" size={18} />
            </button>
          </div>
        )}
        <div className="chats-panel-subrow">
          <span className="chats-panel-sort-label">{searching ? t('tabsView.searchHint', { model: p.search.accountName(p.search.accountId ?? '') }) : t(`tabsView.hint.${p.tab}`)}</span>
          {!searching && <span className="ws-tabs-tools">{p.tools}</span>}
        </div>
        <div className="chats-list-chips" ref={chipDrag.ref} onMouseDown={chipDrag.onMouseDown} onClickCapture={chipDrag.onClickCapture}>
          {WORKSPACE_TABS.map((id) => {
            const on = id === p.tab;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={on}
                className={`chats-list-chip ${on ? 'active' : ''} ${id === 'pinned' ? dropClass(p.drag.overPinned && !pinnedTab) : ''}`}
                data-ws-drop={id === 'pinned' ? 'pinned' : undefined}
                onClick={() => p.onTab(id)}
              >
                {id === 'pinned' && p.drag.active && <FeatherIcon name="pin" size={12} />}
                {t(`tabsView.tabs.${id}`)}
                {id === 'replied' && p.repliedCount > 0 && <span className="ws-tabs-chip-count">{p.repliedCount}</span>}
              </button>
            );
          })}
        </div>
      </div>
      {p.tab === 'activity' && p.activity.reconnecting && !searching && (
        <div className="chat-list-notice" role="status">
          {t('live.reconnectingBanner')}
        </div>
      )}
      <div
        className={`chat-list ${pinnedTab ? dropClass(p.drag.overPinned) : ''}`}
        role="list"
        aria-label={t(`tabsView.tabs.${p.tab}`)}
        data-ws-drop={pinnedTab ? 'pinned' : undefined}
        onMouseEnter={p.tab === 'activity' && !searching ? p.activity.freeze : undefined}
        onMouseLeave={p.tab === 'activity' && !searching ? p.activity.thaw : undefined}
      >
        {p.tab === 'activity' && !searching && p.activity.newCount > 0 && (
          <button type="button" className="ws-new-pill ws-tabs-new" title={t('activity.pausedHint')} onClick={p.activity.freeze}>
            <FeatherIcon name="arrow-up" size={12} />
            {t('activity.newItems', { count: p.activity.newCount })}
          </button>
        )}
        {searching ? (
          <TabRows
            rows={hitRows}
            isActive={isActive}
            onOpen={(row) => {
              const h = hitByKey.get(row.key);
              if (h) p.onOpenHit(h);
            }}
            loading={busy}
            drag={rowDrag}
            empty={error ? <TabEmpty icon="alert-circle" title={error} /> : <TabEmpty icon="search" title={t('search.noResults', { q: query })} />}
          />
        ) : (
          <TabRows
            key={p.tab}
            rows={rows}
            isActive={isActive}
            onOpen={(row) => {
              const card = byKey.get(row.key);
              if (card) p.onOpen(card);
            }}
            loading={p.loading[p.tab]}
            drag={rowDrag}
            empty={empty}
          />
        )}
      </div>
    </div>
  );
});
